import { X } from "lucide-react";
import { CrmOffline } from "./crmApi";
import { API } from "@/services/apiClient";

// Shared building blocks for the admin dashboard — brand-matched (ink/charcoal/weld).

export const Stat = ({ label, value, icon: Icon, accent = false }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-weld/40 bg-weld/10" : "border-white/10 bg-charcoal/60"}`}>
    <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider">
      {Icon && <Icon size={14} className={accent ? "text-weld-glow" : "text-white/40"} />} {label}
    </div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-weld-glow" : "text-white"}`}>{value}</div>
  </div>
);

const TONES = {
  green: "bg-weld/15 text-weld-glow border-weld/30",
  red: "bg-red-500/15 text-red-400 border-red-500/30",
  amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  blue: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  gray: "bg-white/10 text-white/60 border-white/15",
};

export const Pill = ({ children, tone = "gray" }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${TONES[tone] || TONES.gray}`}>
    {children || "—"}
  </span>
);

export const statusTone = (s) => {
  const v = String(s || "").toLowerCase();
  if (["new", "active", "approved", "scheduled", "paid in full", "completed", "converted"].some(x => v === x)) return "green";
  if (["cancelled", "declined", "expired", "lost", "overdue"].some(x => v === x)) return "red";
  if (["due", "pending", "sent", "expiring soon", "deposit required", "partially paid", "in progress", "skipped"].some(x => v === x)) return "amber";
  if (["draft", "deposit paid"].some(x => v === x)) return "blue";
  return "gray";
};

export const Table = ({ cols, rows, onRowClick, testId }) => (
  <div className="overflow-x-auto rounded-xl border border-white/10">
    <table data-testid={testId} className="w-full text-sm min-w-[640px]">
      <thead>
        <tr className="bg-white/5 text-white/50 text-xs uppercase tracking-wider text-left">
          {cols.map(c => <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-white/35">Nothing here yet.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr
            key={r._key || i}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            className={`border-t border-white/5 ${onRowClick ? "cursor-pointer hover:bg-weld/5" : ""}`}
          >
            {cols.map(c => (
              <td key={c.key} className="px-4 py-3 text-white/80 align-top">
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const inputCls =
  "w-full bg-ink/80 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/35 focus:border-weld focus:outline-none focus:ring-1 focus:ring-weld transition text-sm";

export const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-white/50 text-xs uppercase tracking-wider mb-1">{label}</span>
    <input className={inputCls} {...props} />
  </label>
);

export const Sel = ({ label, options, ...props }) => (
  <label className="block">
    <span className="block text-white/50 text-xs uppercase tracking-wider mb-1">{label}</span>
    <select className={inputCls} {...props}>
      {options.map(o => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  </label>
);

export const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-white/50 text-xs uppercase tracking-wider mb-1">{label}</span>
    <textarea rows={3} className={`${inputCls} resize-none`} {...props} />
  </label>
);

export const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
    <div
      data-testid={testId}
      className="bg-charcoal border border-weld/20 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-2xl text-white">{title}</h3>
        <button data-testid="modal-close" onClick={onClose} aria-label="Close" className="text-white/50 hover:text-weld-glow transition-colors">
          <X size={20} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export const Loading = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-8 h-8 border-2 border-weld/30 border-t-weld rounded-full animate-spin" />
  </div>
);

export const Err = ({ error }) => {
  if (error instanceof CrmOffline) {
    return (
      <div data-testid="crm-offline" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm text-amber-200/90 leading-relaxed">
        <p className="font-bold text-amber-300 mb-2">Google CRM not connected yet</p>
        <p>The website side is ready. To bring the CRM online:</p>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>Open <a className="underline text-weld-glow" href={`${API}/crm-script`} target="_blank" rel="noopener noreferrer">the CRM script page</a>, Select All (Ctrl/Cmd+A), Copy — then paste it into a project at <span className="font-semibold">script.google.com</span>.</li>
          <li>Add Script Property <span className="font-mono">API_SECRET</span> with your CRM secret.</li>
          <li>Run <span className="font-mono">setup()</span> once — it creates the spreadsheet, sheets, Drive folders and daily automation.</li>
          <li>Deploy → New deployment → Web app → Execute as: <span className="font-semibold">Me</span>, access: <span className="font-semibold">Anyone</span>.</li>
          <li>Give the new /exec URL to your developer to plug in.</li>
        </ol>
        <p className="mt-3 text-amber-200/60">Technical detail: {error.message}</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{String(error?.message || error)}</div>
  );
};

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const fmtMoney = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? "$0.00" : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Sheets converts time-only cells ("09:00") into epoch serials like
// "1899-12-30T09:00:00.000Z" — extract a clean 12-hour time either way.
export const fmtTime = (v) => {
  if (!v) return "";
  const s = String(v);
  const iso = s.match(/T(\d{2}):(\d{2})/);
  const plain = s.match(/^(\d{1,2}):(\d{2})/);
  const m = iso || plain;
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
};
